import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Crown, Loader2, CalendarClock, RefreshCw, Mail, Check, CreditCard } from "lucide-react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (epoch) =>
  epoch ? new Date(epoch * 1000).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";

const PLAN_LABELS = {
  dreamweaver_monthly: "Premium Monthly ($7/mo)",
  dreamweaver_yearly: "Premium Yearly ($63/yr)",
  dreamweaver_family_monthly: "Family Monthly ($12/mo)",
  dreamweaver_family_yearly: "Family Yearly ($108/yr)",
};

export default function ManagePlan({ user }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewMsg, setPreviewMsg] = useState("");
  const handledReturnRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subscription`, { withCredentials: true });
      setSub(data);
    } catch {
      setSub({ status: "none" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Returning from the Stripe Billing Portal (?billing=return) — confirm any plan
  // switch, cancellation, or resume made there synced back to our own state.
  useEffect(() => {
    if (handledReturnRef.current || !sub || loading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") !== "return") return;
    handledReturnRef.current = true;

    const prev = JSON.parse(sessionStorage.getItem("dw_billing_prev") || "null");
    sessionStorage.removeItem("dw_billing_prev");
    params.delete("billing");
    const newSearch = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));

    if (!prev) return;

    if (prev.lookup_key && sub.lookup_key && prev.lookup_key !== sub.lookup_key) {
      toast.success(`You're now on the ${PLAN_LABELS[sub.lookup_key] || sub.lookup_key} plan!`, {
        description: "Your plan switch from the billing portal synced successfully.",
      });
    } else if (!prev.cancel_at_period_end && sub.cancel_at_period_end) {
      toast.warning("Your subscription is set to cancel.", {
        description: `You'll keep Premium access until ${fmtDate(sub.current_period_end) || "the end of this billing period"} — resume anytime before then.`,
      });
    } else if (prev.cancel_at_period_end && !sub.cancel_at_period_end) {
      toast.success("Your subscription has been resumed!", {
        description: "You're all set — billing continues as normal.",
      });
    } else if (prev.status === "active" && sub.status !== "active") {
      toast("Your subscription has been cancelled.", {
        description: "You'll keep Premium access until the current period ends.",
      });
    }
  }, [sub, loading]);

  const doAction = async (action, path, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(action);
    setError("");
    try {
      const { data } = await axios.post(`${API}${path}`, body || {}, { withCredentials: true });
      if (data.approval_url) {
        window.location.href = data.approval_url;
        return;
      }
      setSub(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (!user) return null;

  const handleManageBilling = async () => {
    setBusy("portal");
    setError("");
    try {
      sessionStorage.setItem("dw_billing_prev", JSON.stringify({
        lookup_key: sub?.lookup_key || null,
        cancel_at_period_end: sub?.cancel_at_period_end || false,
        status: sub?.status || null,
      }));
      const returnUrl = `${window.location.origin}/my-stories?billing=return`;
      const { data } = await axios.post(`${API}/payments/stripe/portal`, { return_url: returnUrl }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not open billing portal. Please try again.");
      setBusy("");
    }
  };

  const sendRenewalPreview = async () => {
    setPreviewBusy(true);
    setPreviewMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/send-renewal-preview`, {}, { withCredentials: true });
      setPreviewMsg(data.message);
    } catch (err) {
      setPreviewMsg(err.response?.data?.detail || "Could not send preview email.");
    } finally {
      setPreviewBusy(false);
      setTimeout(() => setPreviewMsg(""), 5000);
    }
  };

  return (
    <section data-testid="manage-plan-card" className="mt-16">
      <h2 className="text-sm font-medium text-slate-400 tracking-wider uppercase mb-4">Your Plan</h2>
      <div className="glass-card rounded-2xl p-6 sm:p-8">
        {loading ? (
          <Loader2 size={20} className="animate-spin text-amber-400" />
        ) : !user.is_subscribed ? (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[220px]">
              <p className="font-serif text-lg font-bold text-white">Free Dreamer</p>
              <p className="text-xs text-slate-500 mt-1">1 free story · 1 child profile · first-chapter narration</p>
            </div>
            <Link
              to="/#pricing"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all"
            >
              <Crown size={14} /> Upgrade to Premium
            </Link>
          </div>
        ) : sub?.status === "none" ? (
          <div>
            <p className="font-serif text-lg font-bold text-white flex items-center gap-2">
              <Crown size={16} className="text-amber-400" /> DreamWeaver Premium
            </p>
            <p className="text-xs text-slate-500 mt-1">Complimentary premium access — no billing on file.</p>
          </div>
        ) : (
          <div>
            {(() => {
              const tier = sub.lookup_key?.includes("family") ? "family" : "premium";
              const isYear = sub.interval === "year";
              const otherIntervalKey = tier === "family"
                ? (isYear ? "dreamweaver_family_monthly" : "dreamweaver_family_yearly")
                : (isYear ? "dreamweaver_monthly" : "dreamweaver_yearly");
              const otherTierKey = tier === "family"
                ? (isYear ? "dreamweaver_yearly" : "dreamweaver_monthly")
                : (isYear ? "dreamweaver_family_yearly" : "dreamweaver_family_monthly");
              return (
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-lg font-bold text-white flex items-center gap-2">
                      <Crown size={16} className="text-amber-400" />
                      {tier === "family" ? "Family" : "Premium"} — {isYear ? "Yearly" : "Monthly"}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      ${(sub.amount / 100).toFixed(0)}/{isYear ? "year" : "month"}
                    </p>
                    {tier === "family" && (
                      <p className="text-xs text-amber-300/80 mt-1">Unlimited child profiles + shared "Family Favorites" playlist</p>
                    )}
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                      <CalendarClock size={12} />
                      {sub.cancel_at_period_end
                        ? `Ends ${fmtDate(sub.current_period_end)} — you keep premium until then`
                        : `Renews ${fmtDate(sub.current_period_end)}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {sub.provider === "stripe" && (
                      <button
                        data-testid="manage-billing-btn"
                        onClick={handleManageBilling}
                        disabled={!!busy}
                        className="flex items-center gap-2 px-5 py-2 rounded-full border border-purple-500/30 text-slate-300 text-xs font-semibold hover:border-amber-400/40 hover:text-amber-300 transition-colors disabled:opacity-50"
                      >
                        {busy === "portal" ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
                        Manage Billing
                      </button>
                    )}
                    <button
                      data-testid="switch-plan-btn"
                      onClick={() =>
                        doAction(
                          "switch",
                          "/subscription/switch",
                          { lookup_key: otherIntervalKey },
                          `Switch to ${PLAN_LABELS[otherIntervalKey]}? Any difference is prorated.`
                        )
                      }
                      disabled={!!busy}
                      className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-xs font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50"
                    >
                      {busy === "switch" ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      {isYear ? "Switch to Monthly" : "Switch to Yearly — save 25%"}
                    </button>
                    <button
                      data-testid="switch-tier-btn"
                      onClick={() =>
                        doAction(
                          "switch-tier",
                          "/subscription/switch",
                          { lookup_key: otherTierKey },
                          `Switch to ${PLAN_LABELS[otherTierKey]}? Any difference is prorated.`
                        )
                      }
                      disabled={!!busy}
                      className="flex items-center gap-2 px-5 py-2 rounded-full border border-amber-400/40 text-amber-300 text-xs font-semibold hover:bg-amber-500/10 transition-colors disabled:opacity-50"
                    >
                      {busy === "switch-tier" && <Loader2 size={12} className="animate-spin" />}
                      {tier === "family" ? "Switch to Premium" : "Upgrade to Family"}
                    </button>
                    {sub.cancel_at_period_end ? (
                      <button
                        data-testid="resume-plan-btn"
                        onClick={() => doAction("resume", "/subscription/resume")}
                        disabled={!!busy}
                        className="flex items-center gap-2 px-5 py-2 rounded-full border border-emerald-400/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
                      >
                        {busy === "resume" && <Loader2 size={12} className="animate-spin" />}
                        Resume Subscription
                      </button>
                    ) : (
                      <button
                        data-testid="cancel-plan-btn"
                        onClick={() =>
                          doAction("cancel", "/subscription/cancel", null,
                            "Cancel your subscription? You'll keep premium until the end of the current billing period.")
                        }
                        disabled={!!busy}
                        className="px-5 py-2 rounded-full border border-purple-500/30 text-slate-400 text-xs font-medium hover:border-red-400/40 hover:text-red-300 transition-colors disabled:opacity-50"
                      >
                        {busy === "cancel" ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
                        Cancel Plan
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
            {error && <p className="text-xs text-red-400 mt-3">{error}</p>}
          </div>
        )}
      </div>
      {user.role === "admin" && (
        <div className="mt-4 flex items-center gap-3">
          <button
            data-testid="send-renewal-preview-btn"
            onClick={sendRenewalPreview}
            disabled={previewBusy}
            className="flex items-center gap-2 px-5 py-2 rounded-full border border-purple-500/30 text-slate-400 text-xs font-medium hover:border-amber-400/40 hover:text-amber-300 transition-colors disabled:opacity-50"
          >
            {previewBusy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
            Send renewal email preview
          </button>
          {previewMsg && (
            <p data-testid="renewal-preview-msg" className="text-xs text-slate-400 flex items-center gap-1.5">
              {previewMsg.includes("sent") && <Check size={12} className="text-emerald-400" />}
              {previewMsg}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
